import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./use-auth";

export interface SubscriptionStatus {
  hasAccess: boolean;
  isTrialing: boolean;
  daysRemaining: number | null;
  subscriptionStatus: string;
  productId: string | null;
  planName: string | null;
  billingPeriodEnd: string | null;
  scheduledCancellation: boolean;
  cancelAt: string | null;
  applicationsUsed: number;
  applicationsLimit: number;
  lookupsUsed: number;
  lookupsLimit: number;
  enrichedViewsUsed: number;
  enrichedViewsLimit: number | null; // null = unlimited
}

export function useSubscription() {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>({
    hasAccess: false,
    isTrialing: false,
    daysRemaining: null,
    subscriptionStatus: "unknown",
    productId: null,
    planName: null,
    billingPeriodEnd: null,
    scheduledCancellation: false,
    cancelAt: null,
    applicationsUsed: 0,
    applicationsLimit: 0,
    lookupsUsed: 0,
    lookupsLimit: 0,
    enrichedViewsUsed: 0,
    enrichedViewsLimit: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track user ID to prevent re-checking when only user reference changes
  const lastUserIdRef = useRef<string | null>(null);
  const hasCheckedRef = useRef(false);

  const checkSubscription = useCallback(async (forceRefresh = false) => {
    // Skip re-check if user ID hasn't changed and we've already checked (unless force refresh)
    if (!forceRefresh && user?.id && user.id === lastUserIdRef.current && hasCheckedRef.current) {
      return;
    }
    
    if (!user) {
      lastUserIdRef.current = null;
      hasCheckedRef.current = false;
      setStatus({
        hasAccess: false,
        isTrialing: false,
        daysRemaining: null,
        subscriptionStatus: "not_authenticated",
        productId: null,
        planName: null,
        billingPeriodEnd: null,
        scheduledCancellation: false,
        cancelAt: null,
        applicationsUsed: 0,
        applicationsLimit: 0,
        lookupsUsed: 0,
        lookupsLimit: 0,
        enrichedViewsUsed: 0,
        enrichedViewsLimit: null,
      });
      setLoading(false);
      return;
    }

    try {
      // Only show loading on first check, not re-validation
      if (!hasCheckedRef.current) {
        setLoading(true);
      }
      
      lastUserIdRef.current = user.id;
      const { data, error: fnError } = await supabase.functions.invoke("check-subscription");

      if (fnError) {
        console.error("Subscription check error:", fnError);
        setError(fnError.message);
        return;
      }

      if (data.error) {
        console.error("Subscription check error:", data.error);
        setError(data.error);
        return;
      }

      // Handle stale session - server says unauthenticated but we have a local user
      if (data.subscriptionStatus === "unauthenticated" && user) {
        console.warn("Session validation failed - stale session detected");
        await supabase.auth.signOut();
        setStatus({
          hasAccess: false,
          isTrialing: false,
          daysRemaining: null,
          subscriptionStatus: "not_authenticated",
          productId: null,
          planName: null,
          billingPeriodEnd: null,
          scheduledCancellation: false,
          cancelAt: null,
          applicationsUsed: 0,
          applicationsLimit: 0,
          lookupsUsed: 0,
          lookupsLimit: 0,
          enrichedViewsUsed: 0,
          enrichedViewsLimit: null,
        });
        setLoading(false);
        return;
      }

      setStatus({
        hasAccess: data.hasAccess ?? false,
        isTrialing: data.isTrialing ?? false,
        daysRemaining: data.daysRemaining ?? null,
        subscriptionStatus: data.subscriptionStatus ?? "unknown",
        productId: data.productId ?? null,
        planName: data.planName ?? null,
        billingPeriodEnd: data.billingPeriodEnd ?? null,
        scheduledCancellation: data.scheduledCancellation ?? false,
        cancelAt: data.cancelAt ?? null,
        applicationsUsed: data.applicationsUsed ?? 0,
        applicationsLimit: data.applicationsLimit ?? 0,
        lookupsUsed: data.lookupsUsed ?? 0,
        lookupsLimit: data.lookupsLimit ?? 0,
        enrichedViewsUsed: data.enrichedViewsUsed ?? 0,
        enrichedViewsLimit: data.enrichedViewsLimit ?? null,
      });
      setError(null);
      hasCheckedRef.current = true;
    } catch (err) {
      console.error("Subscription check failed:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!authLoading) {
      checkSubscription();
    }
  }, [authLoading, checkSubscription]);

  const createCheckout = async (priceId: string) => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });

      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);

      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Create checkout failed:", err);
      throw err;
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke("customer-portal");

      if (fnError) throw fnError;
      if (data.error) throw new Error(data.error);

      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Customer portal failed:", err);
      throw err;
    }
  };

  return {
    ...status,
    loading: authLoading || loading,
    error,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
  };
}
